import React, { useEffect, useState, useRef } from 'react';
import {
  ActivityIndicator,
  ImageBackground,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RouteProp, useRoute } from '@react-navigation/native';
import Ionicons from '@expo/vector-icons/Ionicons';
import Svg, { Circle, Ellipse, G, Path, SvgXml } from 'react-native-svg';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { RootStackParamList } from '../navigation/types';
import { useAuthScreenRedirect } from '../navigation/authRoutes';
import { api } from '../services/api';
import { useI18n } from '../i18n';
import { analytics } from '../services/analytics';
import { clearSignupFunnel, readSignupFunnel } from '../services/signupFunnel';
import { rememberLiveReturn } from '../services/liveReturnContext';
import { SIGNUP_SOURCE_PROP } from '../services/marketingTags';
import { generateStrongPassword } from '../utils/passwordGenerator';
import { isMobileBrowser, isSafariOnWeb } from '../utils/browserDetection';
import { WebForm } from '../utils/WebForm';
import { SocialAuthButtons } from '../components/auth/SocialAuthButtons';
import { AGAPELLA_LOGO_DARK_XML, AGAPELLA_LOGO_LIGHT_XML } from '../components/auth/agapellaLogoXml';
import { isPasswordValid } from './auth/passwordRules';
import { authErrorKey } from './auth/authErrorCodes';
import { LegalConsentCheckbox, MarketingConsentCheckbox } from './auth/LegalConsent';
import { AgeChoice } from './auth/AgeChoice';
import { narrowFormTitleSize } from './auth/narrowTitleSize';
import { AgeStep, canRegisterWithAge, initialAgeStep, nextAgeStep } from './auth/ageChoiceLogic';
import { LEGAL_DOCUMENTS_VERSION, MARKETING_CONSENT_VERSION, setPendingSignupConsent } from '../services/consents';
import { acceptedDocument, acceptedRegistrationDocuments, isForeignContour, marketingConsentDocuments } from '../legal/accepted';
import { marketingConsentOffered, shouldSendMarketingConsent } from '../services/consentLogic';
import { useLightPathEnabled } from '../hooks/useFeatureFlags';
import { friendlyApiError } from '../services/errorMessages';
import { typographyTokens } from '../styles/typographyTokens';
import { AuthErrorCode } from '../components/auth/AuthErrorCode';
import { resolveAuthErrorCode, copyAuthErrorCode } from '../utils/authErrorCode';
import { ACCENT_TEXT_HEX, ON_ACCENT_TEXT } from '../styles/accentText';

/** Пауза между письмами. Совпадает по духу с ограничением частоты на бэкенде:
 *  кнопка не должна упираться в 429 молча. */
const RESEND_COOLDOWN_S = 60;

type RegisterScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Register'>;
};

export type RegisterPalette = {
  accent: string;
  /** CORE-1733 — оранжевый как цвет текста (заголовок формы, ссылки): `accent`
   *  #F25C05 на светлой карточке давал 3.0–3.1:1 при нужных 4.5. Заливки
   *  кнопок, звёзды и галочки остаются на `accent`. */
  accentText: string;
  /** CORE-1733 (возврат QA 23.10) — «Искатель» в плашке статуса: `accent` #F25C05 давал
   *  3.0:1 в светлой, #FF6A00 над фото в тёмной — 3.5:1. */
  statusName: string;
  background: string;
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  fieldBg: string;
  fieldBorder: string;
  fieldPlaceholder: string;
  helper: string;
  icon: string;
  muted: string;
  statusBg: string;
  statusBorder: string;
  text: string;
  textOnAccent: string;
  errorBg: string;
  errorBorder: string;
  errorText: string;
  errorGlow: string;
  successOrbit: string;
};

type RegisterFieldProps = TextInputProps & {
  label: string;
  palette: RegisterPalette;
  error?: string;
  icon: keyof typeof Ionicons.glyphMap;
  rightIcon?: keyof typeof Ionicons.glyphMap;
  onRightIconPress?: () => void;
  inputStyle?: StyleProp<TextStyle>;
};

const bgLight = require('../../assets/backgrounds/bg-register-light.webp');
const bgDark = require('../../assets/backgrounds/bg-register-dark.webp');

// The approved mockup is a static two-column desktop page verified at
// 1280/1440/1920. Anything narrower (tablets, phones, native) stacks into a
// single fluid column with the same tokens.
const WIDE_LAYOUT_MIN_WIDTH = 1180;

const LIGHT: RegisterPalette = {
  accent: '#F25C05',
  accentText: ACCENT_TEXT_HEX.light,
  statusName: ACCENT_TEXT_HEX.light,
  background: '#FCE6C8',
  cardBg: 'rgba(255, 252, 247, 0.82)',
  cardBorder: 'rgba(227, 169, 107, 0.46)',
  cardShadow: '0 28px 80px rgba(96, 68, 37, 0.14)',
  fieldBg: 'rgba(255, 255, 255, 0.34)',
  fieldBorder: 'rgba(202, 142, 83, 0.30)',
  fieldPlaceholder: 'rgba(28, 34, 40, 0.40)',
  helper: 'rgba(31, 38, 45, 0.55)',
  icon: '#7F8991',
  muted: 'rgba(31, 38, 45, 0.78)',
  statusBg: 'rgba(255, 255, 255, 0.76)',
  statusBorder: 'rgba(242, 92, 5, 0.10)',
  text: '#1C2228',
  // CORE-1733 (возврат QA) — подпись на оранжевой CTA: ON_ACCENT_TEXT (белый 2.8:1).
  textOnAccent: ON_ACCENT_TEXT,
  errorBg: 'rgba(249, 202, 185, 0.56)',
  errorBorder: 'rgba(218, 45, 39, 0.20)',
  errorText: '#C91F1A',
  errorGlow: '0 14px 40px rgba(242, 116, 82, 0.24)',
  successOrbit: 'rgba(242, 92, 5, 0.36)',
};

const DARK: RegisterPalette = {
  accent: '#FF6A00',
  accentText: '#FF6A00', // 5.9–6.6:1 на тёмной карточке — проходит
  statusName: ACCENT_TEXT_HEX.dark, // 5.2:1 и над тёмным фото фона
  background: '#050506',
  cardBg: 'rgba(16, 18, 20, 0.82)',
  cardBorder: 'rgba(255, 106, 0, 0.64)',
  cardShadow: '0 24px 90px rgba(0, 0, 0, 0.52), 0 0 54px rgba(255, 106, 0, 0.13)',
  fieldBg: 'rgba(255, 255, 255, 0.02)',
  fieldBorder: 'rgba(255, 255, 255, 0.18)',
  fieldPlaceholder: 'rgba(247, 244, 239, 0.58)',
  helper: 'rgba(247, 244, 239, 0.58)',
  icon: 'rgba(247, 244, 239, 0.78)',
  muted: 'rgba(247, 244, 239, 0.74)',
  statusBg: 'rgba(8, 9, 10, 0.64)',
  statusBorder: 'rgba(255, 106, 0, 0.34)',
  text: '#F7F4EF',
  // CORE-1733 (возврат QA) — подпись на оранжевой CTA: ON_ACCENT_TEXT (белый 2.8:1).
  textOnAccent: ON_ACCENT_TEXT,
  errorBg: 'rgba(255, 130, 91, 0.12)',
  errorBorder: 'rgba(255, 105, 78, 0.34)',
  errorText: '#FF8A72',
  errorGlow: '0 14px 42px rgba(255, 111, 72, 0.22)',
  successOrbit: 'rgba(255, 106, 0, 0.58)',
};

const StarIcon = ({ size = 24, color = '#F25C05' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M12 1.8l2.45 7.75L22.2 12l-7.75 2.45L12 22.2l-2.45-7.75L1.8 12l7.75-2.45L12 1.8z" fill={color} />
  </Svg>
);

const SuccessOrbit = ({ palette, width = 332 }: { palette: RegisterPalette; width?: number }) => (
  <Svg width={width} height={(width * 154) / 332} viewBox="0 0 332 154">
    <G rotation="-13" origin="166,77">
      <Ellipse
        cx="166"
        cy="77"
        rx="145"
        ry="28"
        fill="none"
        stroke={palette.successOrbit}
        strokeWidth="1.4"
      />
      <Circle cx="253" cy="99" r="4.5" fill={palette.accent} />
    </G>
    <G x="145" y="39">
      <Path d="M20 0l4.2 15.8L40 20l-15.8 4.2L20 40l-4.2-15.8L0 20l15.8-4.2L20 0z" fill={palette.accent} />
    </G>
  </Svg>
);

const BrandLogo = ({ isDark, wide }: { isDark: boolean; wide: boolean }) => (
  <View
    style={[styles.brandLogo, wide ? styles.brandLogoWide : styles.brandLogoNarrow]}
    accessible
    accessibilityRole="image"
    accessibilityLabel="Agapella"
  >
    <SvgXml xml={isDark ? AGAPELLA_LOGO_DARK_XML : AGAPELLA_LOGO_LIGHT_XML} width="100%" height="100%" />
  </View>
);

const RegisterField: React.FC<RegisterFieldProps> = ({
  label,
  palette,
  error,
  icon,
  rightIcon,
  onRightIconPress,
  inputStyle,
  editable,
  ...props
}) => {
  const { t } = useI18n('auth');
  const disabled = editable === false;

  return (
    <View style={styles.fieldBlock}>
      <Text style={[type.label, { color: palette.text, marginBottom: 8 }]}>{label}</Text>
      <View
        style={[
          styles.inputFrame,
          {
            backgroundColor: palette.fieldBg,
            borderColor: error ? palette.errorText : palette.fieldBorder,
          },
          error && styles.inputFrameError,
          disabled && styles.disabled,
        ]}
      >
        <Ionicons name={icon} size={21} color={palette.icon} style={styles.inputIcon} />
        <TextInput
          {...props}
          editable={editable}
          placeholderTextColor={palette.fieldPlaceholder}
          style={[
            styles.input,
            type.input,
            { color: palette.text },
            rightIcon && styles.inputWithRightIcon,
            inputStyle,
          ]}
        />
        {rightIcon ? (
          <TouchableOpacity
            onPress={onRightIconPress}
            disabled={!onRightIconPress || disabled}
            style={styles.inputRightIcon}
            accessibilityRole="button"
            accessibilityLabel={t('register.togglePassword')}
          >
            <Ionicons name={rightIcon} size={20} color={palette.icon} />
          </TouchableOpacity>
        ) : null}
      </View>
      {error ? (
        <View style={styles.fieldErrorRow}>
          <Ionicons name="alert-circle-outline" size={16} color={palette.errorText} />
          <Text style={[type.error, { color: palette.errorText }]}>{error}</Text>
        </View>
      ) : null}
    </View>
  );
};

export const RegisterScreen: React.FC<RegisterScreenProps> = ({ navigation }) => {
  const { t } = useI18n('auth');
  const route = useRoute<RouteProp<RootStackParamList, 'Register'>>();
  const returnTo = route.params?.returnTo;
  // CSS-mask password fields on Safari/WebKit — see browserDetection.
  // Safari ignores `autoComplete="off"` on type=password fields and
  // its iCloud Keychain popup either covers the field (macOS) or
  // blocks the keyboard (iOS), preventing manual password entry.
  // Rendering as `type="text"` + `-webkit-text-security: disc` looks
  // identical to the user and bypasses Keychain entirely.
  const safariMask = isSafariOnWeb();
  const { register, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isDark } = useTheme();
  // CORE-665 — this screen was built around «Путь Света» (CORE-574), but the
  // epic itself is gated by `light_path` (CORE-664). With the flag off the copy
  // promised a status ladder and a progress path that the user then could not
  // find anywhere in their profile. The promise now follows the feature.
  const lightPathEnabled = useLightPathEnabled();
  const palette = isDark ? DARK : LIGHT;
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_LAYOUT_MIN_WIDTH;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // CORE-840 — принятие условий использования, без него регистрация недоступна.
  const [legalAccepted, setLegalAccepted] = useState(false);
  // CORE-1657 — необязательное согласие на рекламные сообщения.
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  // CORE-1404 — возрастной выбор на старте регистрации; форма открывается после него.
  const [ageStep, setAgeStep] = useState<AgeStep>(initialAgeStep);
  const readyAge = canRegisterWithAge(ageStep) ? ageStep.ageGroup : null;
  const ageReady = readyAge !== null;
  // CORE-1929 — вне российского контура рекламную галочку не показываем.
  const foreignContour = isForeignContour();

  // Соцвход создаёт аккаунт без возраста и галочки — экран согласия после входа
  // (ConsentGate) заберёт уже данные здесь ответы и не спросит второй раз.
  useEffect(() => {
    setPendingSignupConsent({ ageGroup: readyAge, legalAccepted, marketingAccepted });
  }, [readyAge, legalAccepted, marketingAccepted]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // CORE-1200 — короткий код неудавшейся попытки и признак «уже скопирован».
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorCodeCopied, setErrorCodeCopied] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  // Приветствие открывается и параметром маршрута: так со страницы входа
  // приходит новый человек, зашедший через Google или Apple (CORE-513).
  const [success, setSuccess] = useState(!!route?.params?.welcome);
  // CORE-647 — почтовая регистрация заканчивается ожиданием письма, а не
  // приветствием: до подтверждения адреса человека в продукт не пускают.
  // Соц-регистрации подтверждать нечего, поэтому вход по параметру маршрута
  // сразу считается социальным — иначе новый человек через Google увидел бы
  // «Проверьте почту», экран из чужого сценария.
  const [successKind, setSuccessKind] =
    useState<'email' | 'social'>(route?.params?.welcome ? 'social' : 'email');
  // Обратный отсчёт до следующей отправки. Нужен и человеку (видно, что
  // произошло), и нам: на бэкенде стоит ограничение частоты, и кнопка, молча
  // упирающаяся в 429, выглядит сломанной.
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((v: number) => v - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  useEffect(() => { analytics.track('signup_page_viewed'); }, []);

  // CORE-1299 — тот же учёт назначения, что и на входе (`LoginScreen`): эфир,
  // из которого человек пришёл заводить аккаунт, должен пережить письмо.
  //
  // Кроме одного случая: приветствие после подтверждения адреса. Туда приводит
  // `ConfirmEmailScreen`, и назначение он уже ВОССТАНОВИЛ из хранилища в
  // параметры маршрута. Сюда мы попадаем не «формой, открытой без эфира», а
  // концом того самого пути — стереть назначение здесь значит сломать возврат
  // ровно на последнем шаге.
  useEffect(() => {
    if (route?.params?.welcome) return;
    void rememberLiveReturn(returnTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // UC-08.02 AC-05: redirect authenticated users away from /register
  //
  // CORE-513 — кроме одного случая: человек только что зарегистрировался через
  // Google или Apple и прямо сейчас смотрит на приветствие Искателя. Соц-вход
  // авторизует сразу, поэтому без этой оговорки экран успеха схлопывался бы
  // мгновенно — человек увидел бы вспышку и главную страницу.
  //
  // QA FAIL 27.08: прежний уход был `goBack()`, а он в свежей вкладке молчит —
  // возвращаться некуда. Правило теперь общее на все экраны входа, см.
  // `navigation/authRoutes`.
  const arrivedAuthenticated = useAuthScreenRedirect({
    navigation,
    isAuthenticated,
    isLoading: authLoading,
    signedInHere: success,
  });

  const validateEmail = (value: string): string | undefined => {
    if (!value) return t('validation.emailRequired');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return t('validation.emailInvalid');
    return undefined;
  };

  const validatePassword = (value: string): string | undefined => {
    if (!value) return t('validation.passwordRequired');
    // CORE-515 — the app's shared password policy (min 8 + one uppercase
    // Latin letter + one digit + printable-ASCII alphabet, from
    // passwordRules.ts). The helper/placeholder copy below mirrors it, and
    // the backend enforces the identical policy (PasswordPolicy.cs).
    if (!isPasswordValid(value)) return t('validation.passwordWeak');
    return undefined;
  };

  const validateConfirmPassword = (value: string): string | undefined => {
    if (!value) return t('validation.confirmPasswordRequired');
    if (value !== password) return t('validation.passwordsMismatch');
    return undefined;
  };

  const clearError = () => {
    setError('');
    setEmailTaken(false);
    setResendSent(false);
    setErrorCode(null);
    setErrorCodeCopied(false);
  };

  const copyErrorCode = async (code: string) => {
    if (await copyAuthErrorCode(code)) setErrorCodeCopied(true);
  };

  // CORE-513 — засов от дублей. Кнопка гаснет по isLoading, но состояние
  // применяется только к следующему рендеру: два быстрых нажатия (или Enter,
  // зажатый в поле) успевают войти сюда дважды и создать второй запрос на
  // регистрацию. Ref срабатывает в тот же тик.
  const submitting = useRef(false);

  const handleSubmit = async () => {
    if (submitting.current) return;
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    const confirmError = validateConfirmPassword(confirmPassword);

    setFieldErrors({
      email: emailError,
      password: passwordError,
      confirmPassword: confirmError,
    });

    if (emailError || passwordError || confirmError) {
      setError(t('register.formError'));
      setEmailTaken(false);
      setResendSent(false);
      return;
    }

    submitting.current = true;
    setIsLoading(true);
    clearError();

    // CORE-1277 — какой CTA привёл к этой форме. Читается один раз и уезжает с
    // обоими событиями: без источника на `signup_submitted` невозможно увидеть
    // отвал между «начал» и «зарегистрировался» в разрезе воронки.
    const funnelSource = await readSignupFunnel();
    const funnelProps = funnelSource ? { [SIGNUP_SOURCE_PROP]: funnelSource } : undefined;

    analytics.track('signup_submitted', funnelProps);
    try {
      await register({
        email,
        password,
        // CORE-1404 — согласие и возраст сохраняются на сервере вместе с аккаунтом.
        personalDataConsent: legalAccepted,
        legalDocumentsVersion: LEGAL_DOCUMENTS_VERSION,
        legalDocuments: acceptedRegistrationDocuments(),
        ageGroup: readyAge ?? undefined,
        ...(shouldSendMarketingConsent(marketingAccepted, readyAge, foreignContour)
          ? {
            marketingConsent: true,
            marketingConsentVersion: MARKETING_CONSENT_VERSION,
            marketingConsentDocument: acceptedDocument('marketing-consent'),
            // CORE-1929 — строка галочки и полный текст согласия, на который она ссылается.
            marketingConsentDocuments: marketingConsentDocuments(),
          }
          : {}),
      });
      analytics.track('signup_success', funnelProps);
      // Регистрация состоялась — метка своё отработала. Оставленная, она
      // приписала бы этому же CTA следующую регистрацию с того же устройства.
      void clearSignupFunnel();
      setSuccessKind('email');
      setResendCooldown(RESEND_COOLDOWN_S);
      setSuccess(true);
    } catch (err) {
      const apiError = err as any;
      const rawMessage = apiError.message || (Array.isArray(apiError.errors) ? apiError.errors[0] : null) || '';
      // CORE-538 — key off the stable BE code (CORE-515). The «already taken»
      // string heuristic stays only as a fallback for responses without code.
      const isTaken = apiError.code === 'EMAIL_ALREADY_EXISTS' || rawMessage.toLowerCase().includes('already taken');
      const codeKey = authErrorKey(apiError);
      const errorMessage = codeKey
        ? t(codeKey)
        : isTaken
          ? t('validation.emailTaken')
          : (rawMessage || t('validation.registerFailed'));
      analytics.track('signup_error', { error_type: errorMessage, code: apiError.code });
      setError(errorMessage);
      // CORE-1200 — правило общее с экраном входа (`resolveAuthErrorCode`):
      // код показывается при НАШЕМ сбое и молчит на «почта уже занята».
      setErrorCode(resolveAuthErrorCode(apiError));
      setEmailTaken(isTaken);
      setResendSent(false);
    } finally {
      submitting.current = false;
      setIsLoading(false);
    }
  };

  // popTo, not navigate: in React Navigation 7 navigate('Home') pushes a copy
  // of Home on top of this transparent modal, and the auth screens beneath
  // keep rendering through it (owner's bug report, 09.10.2026).
  const goHome = () => {
    navigation.popTo('Home');
  };

  const goLogin = () => {
    navigation.replace('Login', { returnTo });
  };

  const hasAnyError = !!error || !!fieldErrors.email || !!fieldErrors.password || !!fieldErrors.confirmPassword;
  const passwordMaskStyle = safariMask && !showPassword
    ? ({ fontFamily: 'text-security-disc', letterSpacing: 2 } as TextStyle)
    : undefined;

  const renderErrorBanner = () => {
    if (!error) return null;

    return (
      <View
        style={[
          styles.errorBanner,
          {
            backgroundColor: palette.errorBg,
            borderColor: palette.errorBorder,
          },
          Platform.OS === 'web' && { boxShadow: palette.errorGlow } as any,
        ]}
        accessibilityLiveRegion="polite"
      >
        <Ionicons name="alert-circle-outline" size={18} color={palette.errorText} />
        <View style={styles.errorTextBlock}>
          <Text style={[type.errorBanner, { color: palette.errorText }]}>{error}</Text>
          {emailTaken && !resendSent ? (
            <TouchableOpacity
              onPress={async () => {
                setResendLoading(true);
                try {
                  await api.resendConfirmation(email);
                  setResendSent(true);
                } catch { /* ignore */ }
                setResendLoading(false);
              }}
              disabled={resendLoading}
              style={styles.resendLink}
              accessibilityRole="button"
            >
              <Text style={[type.link, { color: palette.errorText }]}>
                {resendLoading ? t('register.resending') : t('register.resendConfirmation')}
              </Text>
            </TouchableOpacity>
          ) : null}
          {emailTaken && resendSent ? (
            <Text style={[type.caption, styles.resendSuccess]}>{t('register.resendSuccess')}</Text>
          ) : null}
          {/* CORE-1200 — тот же короткий код попытки, что и на входе. Человек,
              пришедший по приглашению, заводит аккаунт ЗДЕСЬ, и без этого блока
              при сетевом отказе ему нечего было назвать поддержке. */}
          <AuthErrorCode
            code={errorCode}
            copied={errorCodeCopied}
            onCopy={(code) => { void copyErrorCode(code); }}
            testIDPrefix="register"
            containerStyle={styles.errorCodeBlock}
            textStyle={[type.caption, { color: palette.errorText }]}
            hintStyle={[type.caption, { color: palette.errorText, opacity: 0.8 }]}
            linkStyle={[type.link, { color: palette.errorText }]}
            linkContainerStyle={styles.resendLink}
          />
        </View>
      </View>
    );
  };

  const renderForm = () => (
    <>
      <View style={styles.formHeader}>
        <Text accessibilityRole="header" {...(Platform.OS === 'web' ? { 'aria-level': 2 } : {})} style={[type.uiH1, !wide && { fontSize: narrowFormTitleSize(width, FORM_TITLE_PX_PER_FONT_PX) }, { color: palette.text }]}>{t('register.formTitle')}</Text>
        <Text style={[type.body, styles.formIntro, { color: palette.muted }]}>{t('register.formText')}</Text>
      </View>

      <WebForm onSubmit={handleSubmit}>
        <View style={styles.form}>
          {renderErrorBanner()}

          <View style={styles.ageWrap}>
            <AgeChoice
              step={ageStep}
              onAnswer={(answer) => { setAgeStep((s) => nextAgeStep(s, answer)); clearError(); }}
              textColor={palette.text}
              mutedColor={palette.muted}
              accentColor={palette.accentText}
              borderColor={palette.fieldBorder}
              // Mobile web: smaller tiles so «Мне меньше 18» stays on one line.
              compact={!wide}
            />
          </View>

          {ageReady && (<>
          <RegisterField
            testID="register-email-input"
            palette={palette}
            label={t('register.emailLabel')}
            value={email}
            onChangeText={(v: string) => {
              setEmail(v);
              setFieldErrors(prev => ({ ...prev, email: undefined }));
              clearError();
            }}
            placeholder={t('register.emailPlaceholder')}
            icon="mail-outline"
            error={fieldErrors.email}
            autoCapitalize="none"
            autoCorrect={false}
            spellCheck={false}
            keyboardType="email-address"
            editable={!isLoading}
            autoComplete="email"
            textContentType="emailAddress"
          />

          <RegisterField
            testID="register-password-input"
            palette={palette}
            label={t('register.passwordLabel')}
            value={password}
            onChangeText={(v: string) => {
              setPassword(v);
              setFieldErrors(prev => ({ ...prev, password: undefined }));
              clearError();
            }}
            placeholder={t('register.passwordPlaceholder')}
            icon="lock-closed-outline"
            rightIcon={showPassword ? 'eye-off-outline' : 'eye-outline'}
            onRightIconPress={() => setShowPassword(!showPassword)}
            secureTextEntry={safariMask ? false : !showPassword}
            autoCorrect={false}
            autoCapitalize="none"
            spellCheck={false}
            error={fieldErrors.password}
            editable={!isLoading}
            autoComplete="off"
            inputMode="text"
            inputStyle={passwordMaskStyle}
          />

          <View style={styles.passwordMetaBlock}>
            <Text style={[type.caption, { color: palette.helper }]}>{t('register.passwordHelper')}</Text>
            <TouchableOpacity
              onPress={() => {
                const generated = generateStrongPassword();
                setPassword(generated);
                setConfirmPassword(generated);
                setShowPassword(true);
                setFieldErrors(prev => ({ ...prev, password: undefined, confirmPassword: undefined }));
                clearError();
                analytics.track('signup_password_generated');
              }}
              disabled={isLoading}
              activeOpacity={0.72}
              style={styles.generatePasswordLink}
              accessibilityRole="button"
              accessibilityLabel={t('register.generatePassword')}
            >
              <StarIcon size={15} color={palette.accent} />
              <Text style={[type.link, { color: palette.accentText }]}>{t('register.generatePassword')}</Text>
            </TouchableOpacity>
          </View>

          <RegisterField
            testID="register-confirm-password-input"
            palette={palette}
            label={t('register.confirmPasswordLabel')}
            value={confirmPassword}
            onChangeText={(v: string) => {
              setConfirmPassword(v);
              setFieldErrors(prev => ({ ...prev, confirmPassword: undefined }));
              clearError();
            }}
            placeholder={t('register.passwordPlaceholder')}
            icon="lock-closed-outline"
            rightIcon={showPassword ? 'eye-off-outline' : 'eye-outline'}
            onRightIconPress={() => setShowPassword(!showPassword)}
            secureTextEntry={safariMask ? false : !showPassword}
            autoCorrect={false}
            autoCapitalize="none"
            spellCheck={false}
            error={fieldErrors.confirmPassword}
            editable={!isLoading}
            autoComplete="off"
            inputMode="text"
            inputStyle={passwordMaskStyle}
          />

          {/* CORE-840 — согласие с Пользовательским соглашением обязательно до
              создания аккаунта; на вебе документы открываются маршрутом /legal/:docId. */}
          <View style={styles.consentWrap}>
            <LegalConsentCheckbox
              checked={legalAccepted}
              onToggle={() => { setLegalAccepted((v) => !v); clearError(); }}
              onOpenDoc={(docId) => navigation.navigate('LegalDoc', { docId })}
              textColor={palette.muted}
              linkColor={palette.accentText}
              borderColor={palette.fieldBorder}
              fontSize={14}
            />
            {/* CORE-1657 — рекламная галочка необязательна; подросткам не предлагается.
                CORE-1929 — и вне российского контура; ссылка ведёт на полный текст. */}
            {marketingConsentOffered(readyAge, foreignContour) && (
              <MarketingConsentCheckbox
                checked={marketingAccepted}
                onToggle={() => setMarketingAccepted((v) => !v)}
                onOpenDoc={(docId) => navigation.navigate('LegalDoc', { docId })}
                textColor={palette.muted}
                accentColor={palette.accent}
                linkColor={palette.accentText}
                borderColor={palette.fieldBorder}
                fontSize={14}
              />
            )}
          </View>

          <TouchableOpacity
            testID="register-submit-button"
            onPress={handleSubmit}
            disabled={isLoading || !legalAccepted}
            activeOpacity={0.88}
            style={[
              styles.primaryButton,
              { backgroundColor: palette.accent },
              (isLoading || !legalAccepted) && styles.disabled,
            ]}
            accessibilityRole="button"
          >
            {isLoading && <ActivityIndicator color={palette.textOnAccent} style={{ marginRight: 8 }} />}
            <Text style={[type.buttonPrimary, { color: palette.textOnAccent }]}>
              {hasAnyError
                ? t('register.retry')
                : t(lightPathEnabled ? 'register.submit' : 'register.submitPlain')}
            </Text>
          </TouchableOpacity>

          <SocialAuthButtons
            onError={setError}
            appearance="register"
            onSuccess={({ isNewUser }) => {
              // CORE-513 — новый человек через Google/Apple видит то же
              // приветствие Искателя, что и зарегистрировавшийся почтой:
              // способ входа не должен менять момент знакомства с платформой.
              // Существующего ведём обычным путём — эффект выше уведёт его с
              // /register, как только состояние авторизации обновится.
              if (isNewUser) {
                // Источник воронки читается и здесь: цель называется
                // «регистрация через <CTA>», а не «регистрация почтой». Человек,
                // нажавший «Начать путь» и заведший аккаунт через Google, прошёл
                // ту же воронку — способ входа к ней отношения не имеет.
                void readSignupFunnel().then((source) => {
                  analytics.track('signup_success', {
                    method: 'social',
                    ...(source ? { [SIGNUP_SOURCE_PROP]: source } : {}),
                  });
                  void clearSignupFunnel();
                });
                // Вид успеха обязателен: по умолчанию он «email», и без этой
                // строки новый человек через Google увидел бы «Проверьте
                // почту» — экран из чужого сценария, ему подтверждать нечего.
                // Слияние CORE-513 и CORE-647 проходит текстом, а эту подмену
                // не ловит: конфликта нет, поведение ломается.
                setSuccessKind('social');
                setSuccess(true);
              }
            }}
          />
          </>)}

          <View style={styles.footerRow}>
            <Text style={[type.body, { color: palette.muted }]}>{t('register.alreadyHaveAccount')}</Text>
            <TouchableOpacity onPress={goLogin} activeOpacity={0.72} accessibilityRole="button">
              <Text style={[type.link, { color: palette.accentText }]}>{t('register.signIn')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </WebForm>
    </>
  );

  /**
   * CORE-647 — «Проверьте почту». Показывается вместо приветствия, когда
   * человек зарегистрировался почтой: до подтверждения адреса он в продукт не
   * попадает, и звать его «в пространство Agapella» было бы обманом — по
   * кнопке он упёрся бы в отказ входа.
   *
   * Раскладка и стили те же, что у приветствия (макет CORE-574) — меняется
   * только содержимое.
   */
  const renderCheckMail = () => (
    <View style={styles.successContent}>
      <View style={[styles.successOrbit, !wide && styles.successOrbitNarrow]}>
        <SuccessOrbit palette={palette} width={wide ? 332 : 272} />
      </View>

      <View style={styles.successTitleWrap}>
        <Text style={[type.displayH2, styles.successTitle, { color: palette.text }]}>
          {t('register.checkMailTitle')}
        </Text>
      </View>

      <Text style={[type.body, styles.successLead, !wide && styles.successLeadNarrow, { color: palette.muted }]}>
        {t('register.checkMailLead', { email })}
      </Text>
      <Text style={[type.body, styles.successBody, !wide && styles.successBodyNarrow, { color: palette.muted }]}>
        {t('register.checkMailHint')}
      </Text>

      <TouchableOpacity
        testID="register-resend-confirmation"
        disabled={resendLoading || resendCooldown > 0}
        onPress={async () => {
          setResendLoading(true);
          setResendSent(false);
          try {
            await api.resendConfirmation(email);
            setResendSent(true);
            setResendCooldown(RESEND_COOLDOWN_S);
          } catch (e) {
            setError(friendlyApiError(e));
          } finally {
            setResendLoading(false);
          }
        }}
        activeOpacity={0.88}
        style={[
          styles.primaryButton,
          styles.successButton,
          !wide && styles.resendButtonNarrow,
          { backgroundColor: palette.accent },
          (resendLoading || resendCooldown > 0) && { opacity: 0.5 },
        ]}
        accessibilityRole="button"
      >
        <Text style={[type.buttonPrimary, !wide && styles.resendLabelNarrow, { color: palette.textOnAccent }]}>
          {resendLoading
            ? t('register.resending')
            : resendCooldown > 0
              ? t('register.resendIn', { seconds: resendCooldown })
              : t('register.resendConfirmation')}
        </Text>
      </TouchableOpacity>

      {resendSent ? (
        <Text style={[type.body, { color: palette.helper, marginTop: 12, textAlign: 'center' }]}>
          {t('register.resendSuccess')}
        </Text>
      ) : null}
      {error ? (
        <Text style={[type.body, { color: '#D9534F', marginTop: 12, textAlign: 'center' }]}>
          {error}
        </Text>
      ) : null}

      <TouchableOpacity
        testID="register-change-email"
        onPress={() => {
          // Ошибиться в адресе легко, и тупик здесь недопустим: возвращаем к
          // форме с уже введёнными данными, чтобы поправить одну букву, а не
          // заполнять всё заново.
          setSuccess(false);
          setResendSent(false);
          setError('');
        }}
        activeOpacity={0.72}
        style={styles.successNext}
        accessibilityRole="button"
      >
        <Text style={[type.body, styles.successNextText, { color: palette.helper }]}>
          {t('register.changeEmail')}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderSuccess = () => (
    <View style={styles.successContent}>
      <View style={[styles.successOrbit, !wide && styles.successOrbitNarrow]}>
        <SuccessOrbit palette={palette} width={wide ? 332 : 272} />
      </View>

      <View style={styles.successTitleWrap}>
        <Text style={[type.displayH2, styles.successTitle, { color: palette.text }]}>
          {t('register.welcomeTitle')}
        </Text>
        <View style={styles.successTitleLine}>
          <Text style={[type.displayH2, styles.successTitle, { color: palette.text }]}>
            {t(lightPathEnabled ? 'register.statusName' : 'register.successNamePlain')}
          </Text>
          <View style={styles.successSpark}>
            <StarIcon size={26} color={palette.accent} />
            <StarIcon size={18} color={palette.accent} />
          </View>
        </View>
      </View>

      <Text style={[type.body, styles.successLead, { color: palette.muted }]}>
        {t(lightPathEnabled ? 'register.successLead' : 'register.successLeadPlain')}
      </Text>
      <Text style={[type.body, styles.successBody, !wide && styles.successBodyNarrow, { color: palette.muted }]}>
        {t('register.successText')}
      </Text>

      <TouchableOpacity
        testID="register-continue-to-login-button"
        onPress={goLogin}
        activeOpacity={0.88}
        style={[styles.primaryButton, styles.successButton, { backgroundColor: palette.accent }]}
        accessibilityRole="button"
      >
        <Text style={[type.buttonPrimary, { color: palette.textOnAccent }]}>{t('register.successButton')}</Text>
      </TouchableOpacity>

      <TouchableOpacity
        onPress={goHome}
        activeOpacity={0.72}
        style={styles.successNext}
        accessibilityRole="button"
      >
        <Text style={[type.body, styles.successNextText, { color: palette.helper }]}>{t('register.successNext')}</Text>
        <Ionicons name="arrow-forward-outline" size={26} color={palette.accent} />
      </TouchableOpacity>

      {/* CORE-1289 — ссылка из письма на телефоне может открыться в браузере
          (iOS без Universal Links, Android с неподтверждённым assetlinks).
          Почта уже подтверждена здесь, поэтому в приложение зовём сразу на
          вход по своей схеме `agapella://` — она работает без верификации
          домена. Только в мобильном браузере: на десктопе кнопке некуда вести. */}
      {isMobileBrowser() && (
        <TouchableOpacity
          testID="register-open-in-app"
          onPress={() => {
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
              window.location.href = 'agapella://login';
            }
          }}
          activeOpacity={0.72}
          style={styles.successNext}
          accessibilityRole="link"
        >
          <Text style={[type.body, styles.successNextText, { color: palette.helper }]}>
            {t('register.openInApp')}
          </Text>
          <Ionicons name="phone-portrait-outline" size={22} color={palette.accent} />
        </TouchableOpacity>
      )}
    </View>
  );

  // CORE-513 AC-12 — авторизованному форма не показывается ВОВСЕ, а не
  // убирается следом за навигацией. Эффект выше уводит его отсюда, но полагаться
  // на побочный эффект здесь нельзя: пока он не отработал, человек смотрит на
  // поля «Email / Пароль» и кнопку создания аккаунта, которых для него не
  // существует. QA снял это на деве именно так — форма осталась на экране.
  //
  // Исключение то же, что у эффекта: только что вошедший через Google или Apple
  // стоит на приветствии Искателя, и оно — законный успех, а не форма.
  if (arrivedAuthenticated) return null;

  return (
    <ImageBackground
      source={isDark ? bgDark : bgLight}
      resizeMode="cover"
      style={[styles.background, { backgroundColor: palette.background }]}
      imageStyle={styles.backgroundImage}
    >
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          horizontal={false}
        >
          <TouchableOpacity
            style={[styles.backButton, wide ? styles.backButtonWide : styles.backButtonNarrow]}
            onPress={goHome}
            activeOpacity={0.72}
            accessibilityRole="button"
            accessibilityLabel={t('register.backHome')}
          >
            <Ionicons name="arrow-back-outline" size={30} color={palette.text} />
            <Text style={[type.navigation, { color: palette.text }]}>{t('register.backHome')}</Text>
          </TouchableOpacity>

          <View style={[styles.pageFrame, wide ? styles.pageFrameWide : styles.pageFrameNarrow]}>
            <View style={[wide ? styles.leftColumnWide : styles.leftColumnNarrow]}>
              <BrandLogo isDark={isDark} wide={wide} />
              <Text
                accessibilityRole="header"
                style={[
                  type.displayH1,
                  // Mobile web: same size as the login / reset heroes (displayH2 - 3px).
                  !wide && type.displayH2,
                  styles.heroTitle,
                  !wide && styles.heroTitleNarrow,
                  { color: palette.text },
                ]}
              >
                {t(lightPathEnabled ? 'register.heroTitle' : 'register.heroTitlePlain')}
              </Text>
              <Text style={[type.bodyLarge, styles.heroBody, { color: palette.muted }]}>
                {t('register.heroText')}
              </Text>
              {/* CORE-665 — the «первый статус» badge is the most concrete promise
                  on this screen: it names a level from a ladder the user will not
                  find anywhere if the epic is off. */}
              {lightPathEnabled && (
              <View
                style={[
                  styles.statusBadge,
                  {
                    backgroundColor: palette.statusBg,
                    borderColor: palette.statusBorder,
                  },
                ]}
              >
                <StarIcon size={25} color={palette.accent} />
                <Text style={[type.body, styles.statusBadgeText, { color: palette.text }]}>
                  {t('register.statusPrefix')}
                  <Text style={[type.bodyAccent, { color: palette.statusName }]}>{t('register.statusName')}</Text>
                </Text>
              </View>
              )}
            </View>

            <View
              style={[
                styles.card,
                wide ? styles.cardWide : styles.cardNarrow,
                {
                  backgroundColor: palette.cardBg,
                  borderColor: palette.cardBorder,
                },
                Platform.OS === 'web' && { boxShadow: palette.cardShadow, backdropFilter: 'blur(24px)' } as any,
                success && (wide ? styles.cardSuccessWide : styles.cardSuccessNarrow),
              ]}
            >
              {success ? (successKind === 'email' ? renderCheckMail() : renderSuccess()) : renderForm()}
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ImageBackground>
  );
};

// «Создать аккаунт» in Nunito Sans 700 needs ≈8.124 px of width per 1 px of
// font size (measured 09.10.2026) — see narrowTitleSize.ts.
const FORM_TITLE_PX_PER_FONT_PX = 8.124;

const type = {
  ...typographyTokens,
  errorBanner: typographyTokens.error,
};

const styles = StyleSheet.create({
  background: {
    flex: 1,
  },
  backgroundImage: {
    width: '100%',
    height: '100%',
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    minHeight: Platform.OS === 'web' ? '100vh' as any : undefined,
  },
  backButton: {
    position: 'absolute',
    zIndex: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  } as any,
  backButtonWide: {
    top: 46,
    left: 48,
  },
  backButtonNarrow: {
    top: 18,
    left: 16,
  },
  pageFrame: {
    width: '100%',
    alignSelf: 'center',
  },
  // The two columns are 760 + 604 with a 72 gap and 24 of side padding, i.e.
  // 1484px of hard requirement — but WIDE_LAYOUT_MIN_WIDTH turns this layout on
  // at 1180. Between the two the frame was wider than the window, and since the
  // shell clips instead of scrolling, the hero was cut off on the left and the
  // card on the right at 1280 / 1366 / 1440 — the three commonest laptop widths.
  // No minWidth here, and the left column below flexes, so the row fits from the
  // breakpoint up.
  pageFrameWide: {
    maxWidth: 1556,
    minHeight: Platform.OS === 'web' ? '100vh' as any : 900,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 72,
    paddingHorizontal: 24,
    paddingVertical: 48,
  } as any,
  pageFrameNarrow: {
    maxWidth: 652,
    flexDirection: 'column',
    gap: 36,
    paddingHorizontal: 20,
    paddingTop: 84,
    paddingBottom: 36,
  },
  leftColumnWide: {
    // Flexes 760 → whatever is left once the card has its 604; `minWidth: 0`
    // because a flex child defaults to min-content, which would refuse to
    // shrink below the longest word and push the row wide again.
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 760,
    minWidth: 0,
    maxWidth: 760,
    alignSelf: 'flex-start',
    paddingTop: 105,
    paddingLeft: 10,
  },
  leftColumnNarrow: {
    width: '100%',
  },
  brandLogo: {
    alignSelf: 'flex-start',
  },
  brandLogoWide: {
    width: 315,
    height: 84,
    marginBottom: 28,
  },
  brandLogoNarrow: {
    width: 224,
    height: 60,
    marginBottom: 20,
  },
  heroTitle: {
    maxWidth: 720,
    marginBottom: 28,
  },
  // Mobile web only: the hero heading sits 3px below the displayH2 token
  // (owner's request, 09.10.2026, in step with LoginScreen / ForgotPassword).
  heroTitleNarrow: {
    fontSize: 37,
  },
  heroBody: {
    maxWidth: 620,
    marginBottom: 28,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    maxWidth: '100%',
    minHeight: 54,
    borderRadius: 27,
    borderWidth: 1,
    paddingLeft: 21,
    paddingRight: 27,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  statusBadgeText: {
    flexShrink: 1,
  },
  card: {
    borderRadius: 24,
    borderWidth: 1,
  } as any,
  cardWide: {
    width: 604,
    // The card keeps its exact width — its 96 of inner padding is what the
    // field/button rhythm is built on. The hero column absorbs the difference.
    flexShrink: 0,
    minHeight: 830,
    paddingHorizontal: 96,
    paddingTop: 46,
    paddingBottom: 38,
  },
  cardNarrow: {
    width: '100%',
    maxWidth: 604,
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingTop: 36,
    paddingBottom: 28,
  },
  cardSuccessWide: {
    paddingHorizontal: 76,
    paddingTop: 70,
    paddingBottom: 56,
  },
  cardSuccessNarrow: {
    paddingTop: 48,
    paddingBottom: 40,
  },
  formHeader: {
    marginBottom: 24,
  },
  formIntro: {
    marginTop: 10,
  },
  form: {
    width: '100%',
  },
  errorBanner: {
    minHeight: 48,
    borderRadius: 24,
    borderWidth: 1,
    marginBottom: 18,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  errorTextBlock: {
    flex: 1,
  },
  errorCodeBlock: { marginTop: 8, gap: 2 },
  resendLink: {
    marginTop: 6,
    alignSelf: 'flex-start',
  },
  resendSuccess: {
    marginTop: 6,
    color: '#22C55E',
  } as any,
  fieldBlock: {
    marginTop: 0,
    marginBottom: 20,
  },
  inputFrame: {
    height: 52,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    ...(Platform.OS === 'web' ? {
      transition: 'border-color 160ms ease, box-shadow 160ms ease',
    } : {}),
  } as any,
  inputFrameError: {
    borderWidth: 1.2,
  },
  inputIcon: {
    marginRight: 12,
  },
  input: {
    flex: 1,
    height: '100%',
    paddingVertical: 0,
    ...(Platform.OS === 'web' ? {
      outlineStyle: 'none',
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 0,
    } : {}),
  } as any,
  inputWithRightIcon: {
    paddingRight: 36,
  },
  inputRightIcon: {
    position: 'absolute',
    right: 16,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  } as any,
  fieldErrorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
  },
  // The approved helper copy (full CORE-515 policy) no longer fits on one
  // line next to the generate link at the card's content width, so the meta
  // block stacks: helper first, link right-aligned below it.
  passwordMetaBlock: {
    marginTop: -10,
    marginBottom: 28,
    gap: 6,
  },
  generatePasswordLink: {
    alignSelf: 'flex-end',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  } as any,
  primaryButton: {
    flexDirection: 'row',
    width: '100%',
    height: 56,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F45122',
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? {
      cursor: 'pointer',
      backgroundImage: 'linear-gradient(90deg, #F45122 0%, #FF955D 100%)',
      boxShadow: '0 10px 30px -5px rgba(233, 130, 69, 0.40)',
      transition: 'transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease',
    } as any : {
      elevation: 8,
    }),
  } as any,
  disabled: {
    opacity: 0.58,
  },
  ageWrap: {
    marginBottom: 20,
  },
  consentWrap: {
    marginBottom: 20,
    gap: 12,
  },
  footerRow: {
    marginTop: 28,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  footerLink: {
    ...typographyTokens.link,
  },
  successContent: {
    flex: 1,
    alignItems: 'center',
  },
  successOrbit: {
    marginBottom: 74,
  },
  successOrbitNarrow: {
    marginBottom: 44,
  },
  successTitleWrap: {
    alignItems: 'center',
    marginBottom: 18,
  },
  successTitleLine: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 12,
  },
  successTitle: {
    textAlign: 'center',
  },
  successSpark: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
    paddingBottom: 6,
  },
  successLead: {
    textAlign: 'center',
    marginBottom: 24,
  },
  // CORE-1906 — длинный адрес в «Мы отправили письмо на …»: блок по центру не
  // бывает уже самого адреса и вылезал за карточку. Во всю ширину карточки
  // адрес переносится внутри неё; обычный текст выглядит как раньше.
  successLeadNarrow: {
    alignSelf: 'stretch',
  },
  successBody: {
    textAlign: 'center',
    marginBottom: 76,
  },
  successBodyNarrow: {
    marginBottom: 44,
  },
  successButton: {
    maxWidth: 446,
  },
  // CORE-1906 — «Отправить повторно можно через N с» на телефоне в одну строку
  // не помещается, а у primaryButton жёсткие 56 px и нет полей: текст шёл от
  // края до края, и скругление срезало крайние буквы. Кнопка растёт под
  // вторую строку и держит поля. Поля 20, а не 24: с 24 на ширине 360 после
  // отсчёта в две строки уходила и «Отправить письмо повторно» (223 px).
  resendButtonNarrow: {
    height: 'auto',
    minHeight: 56,
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  // Две ровные строки вместо таймера, оторванного на свою строку. Где
  // text-wrap: balance нет (Chrome < 114, iOS < 17.5), перенос обычный.
  resendLabelNarrow: {
    textWrap: 'balance',
  } as any,
  successNext: {
    width: '100%',
    maxWidth: 446,
    marginTop: 34,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  } as any,
  successNextText: {
    flexShrink: 1,
  },
});

// CORE-668 — the web login page is the same visual system as this page
// (identical tokens, field frames, card and layout grid), so the building
// blocks are exported from here instead of being duplicated there.
export {
  LIGHT as REGISTER_LIGHT,
  DARK as REGISTER_DARK,
  bgLight as REGISTER_BG_LIGHT,
  bgDark as REGISTER_BG_DARK,
  WIDE_LAYOUT_MIN_WIDTH,
  StarIcon,
  BrandLogo,
  RegisterField,
  type as registerType,
  styles as registerStyles,
};
